import React from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { HOLIDAY_TYPES } from './bibliothequeConstants';

const formatDate = (dateString, language) =>
  new Date(dateString).toLocaleDateString(language, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

const BibliothequeTableCard = ({ loading, holidays, totalPages, page, setPage, handleEdit, handleDelete }) => {
  const { t, i18n } = useTranslation();

  return (
    <>
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("holidayCommon.fields.name")}</TableHead>
              <TableHead>{t("holidayCommon.fields.date")}</TableHead>
              <TableHead>{t("holidayCommon.fields.type")}</TableHead>
              <TableHead>{t("holidayCommon.fields.country")}</TableHead>
              <TableHead>{t("holidayLibraryComponents.table.recurring")}</TableHead>
              <TableHead className="text-right">{t("holidayCommon.fields.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">{t("holidayCommon.loading")}</TableCell>
              </TableRow>
            ) : holidays.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  {t("holidayLibraryComponents.table.empty")}
                </TableCell>
              </TableRow>
            ) : (
              holidays.map((holiday) => (
                <TableRow key={holiday.id}>
                  <TableCell className="font-medium">{holiday.name}</TableCell>
                  <TableCell>{formatDate(holiday.date, i18n.language)}</TableCell>
                  <TableCell>
                    <Badge className={HOLIDAY_TYPES[holiday.holiday_type || holiday.type]?.color}>
                      {t(`holidayCommon.types.${holiday.holiday_type || holiday.type}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>{holiday.country}</TableCell>
                  <TableCell>
                    {holiday.is_recurring ? (
                      <Badge variant="outline">{t("holidayCommon.yes")}</Badge>
                    ) : (
                      <span className="text-muted-foreground">{t("holidayCommon.no")}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(holiday)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(holiday.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>

    {totalPages > 1 && (
      <div className="flex justify-center gap-2">
        <Button variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
          {t("holidayCommon.previous")}
        </Button>
        <span className="flex items-center px-4">{t("holidayCommon.page", { page, total: totalPages })}</span>
        <Button variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
          {t("holidayCommon.next")}
        </Button>
      </div>
    )}
    </>
  );
};

export default BibliothequeTableCard;
